import { useMutation, useQuery } from '@tanstack/react-query'
import { previousFriday, previousThursday, startOfDay } from 'date-fns'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { GetCampaignFragment } from '@/types/campaign'
import { useCampaignClient, useEndUsersIntegrationsClient } from '@/lib/api/ts-rest-client'
import { Button, DateRangePicker } from '@alphabait/ui'
import { EndUsersUploadedError } from '@/types/common'
import { toUtcEndDay, toUtcStartDay } from '../../../../lib/utils'

export default function OneCommunityPurchases({
  campaign,
  onComplete,
  setErrors,
  setShowModal,
}: {
  campaign: GetCampaignFragment
  onComplete: () => void
  setErrors: (errors?: EndUsersUploadedError[]) => void
  setShowModal: (show: boolean) => void
}) {
  const campaignClient = useCampaignClient()
  const integrationsClient = useEndUsersIntegrationsClient()

  const { data: purchaseDateRange } = useQuery({
    queryKey: ['latestPurchaseDateRange', campaign.id],
    queryFn: async () => {
      const response = await campaignClient.latestPurchaseDateRange.query({
        params: { id: campaign.id },
      })
      return response.status === 200 ? response.body : null
    },
  })

  const hasOneCommunityPurchasesIntegration = useMemo(
    () =>
      campaign?.campaignCategory === 'generic' &&
      campaign?.customer?.integrations?.some(
        (i: any) => i?.type === 'ONE_COMMUNITY' && i.features?.purchases?.enabled,
      ),
    [campaign?.campaignCategory, campaign?.customer?.integrations],
  )

  const { t } = useTranslation()

  const [dateRange, setDateRange] = useState<
    { from: Date; to: Date } | undefined
  >(
    campaign.optionalSettings?.oneCommunity?.dateRange
      ? {
        from: new Date(
          campaign.optionalSettings.oneCommunity.dateRange.from.substring(
            0,
            10,
          ),
        ),
        to: new Date(
          campaign.optionalSettings.oneCommunity.dateRange.to.substring(
            0,
            10,
          ),
        ),
      }
      : {
        from: startOfDay(previousFriday(previousThursday(new Date()))),
        to: startOfDay(previousThursday(new Date())),
      },
  )

  const fetchPurchasesMutation = useMutation({
    mutationFn: async (data: { campaignId: string; dateRange: { from: string; to: string } }) => {
      const response = await integrationsClient.getPurchases.mutate({
        params: { campaignId: data.campaignId },
        body: data.dateRange,
      })

      // Handle validation errors (400 responses)
      if (response.status === 400) {
        throw response.body
      }

      return response
    },
    onSuccess: () => {
      onComplete()
    },
    onError: (error: any) => {
      // Backend returns array of errors directly
      if (Array.isArray(error)) {
        setErrors(error)
        setShowModal(true)
        return
      }

      console.error('Purchases error:', error)
    },
  })

  if (!hasOneCommunityPurchasesIntegration) return <></>

  return (
    <div className="flex flex-row justify-between bg-ui-100 rounded-lg py-4 px-2 items-center">
      <div className="text-gray-200 text-sm">
        <>
          <h1 className="px-1 text-base font-bold">
            {t('api_integration.generic_header')}
          </h1>
          <p className="p-1 text-sm font-normal">
            {t('api_integration.generic_text')}
          </p>
        </>

        <div className="w-1/5 grid">
          <DateRangePicker
            value={dateRange}
            setValue={(d) => {
              if (d?.from && d?.to) setDateRange({ from: d.from, to: d.to })
            }}
            latestPurchaseDateRange={purchaseDateRange || undefined}
          />
        </div>
      </div>
      <Button
        onClick={() => {
          if (dateRange?.from && dateRange?.to) {
            fetchPurchasesMutation.mutate({
              campaignId: campaign.id,
              dateRange: {
                from: toUtcStartDay(dateRange.from).toISOString(),
                to: toUtcEndDay(dateRange.to).toISOString(),
              },
            })
          }
        }}
        className="flex flex-row cursor-pointer text-white"
        disabled={fetchPurchasesMutation.isPending || !dateRange}
      >
        {fetchPurchasesMutation.isPending ? 'Loading...' : t('api_integration.sync')}
      </Button>
    </div>
  )
}
